import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  ThemeProvider,
  Snackbar,
  Alert,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Link,
  Autocomplete,
  TextField


} from '@mui/material';
import { getBillingRecords } from '../API/billingAPI';
import { getCacheClients, getClient } from "../API/clientAPI";
import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import EventIcon from "@mui/icons-material/Event";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Add as AddIcon } from "@mui/icons-material";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: theme.spacing(4, 0),
  opacity: 0.8,
}));


function ViewClientBillingRecords() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingRecords, setBillingRecords] = useState([])
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [clientsCache, setClientsCache] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isFetchingClient, setIsFetchingClient] = useState(false);

  const navigate = useNavigate();

  const [searchFilter, setSearchFilter] = useState({
    client: "",
    start_date: "",
    end_date: "",  // Track selected payment status
  });

  const [summaryCount, setSummaryCount] = useState(
    {
      total: "",
      not_billed: "",
      billed: "",
      not_due: "",
      over_due: ""
    }
  )

  useEffect(() => {
    const fetchCacheClients = async () => {
      try {
        // Fetch clients
        const clientsCache = await getCacheClients(); // Pass status if needed

        setClientsCache(clientsCache);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCacheClients();
  }, []);





  const handleViewClick = (billingRecord) => {
    navigate(`/view-billing-record`, { state: { billingRecord } });
  };
  const handleEditClick = (billingRecord) => {
    navigate(`/edit-billing-record`, { state: { billingRecord } });
  };

  const handleAddWorkNotesClick = (billingRecord) => {
    navigate(`/add-billing-record-work-notes`, { state: { billingRecord } });
  };

  const handleSearchClient = (event, value) => {
    setSearchFilter((prev) => ({
      ...prev,
      client: value,
    }));
  };

  const handleSearchButtonClick = async () => {
    setIsLoading(true)
    try {
      const records = await getBillingRecords(
        searchFilter.client?.id || '',
        searchFilter.payment_status || '',
        searchFilter.start_date || '',
        searchFilter.end_date || '',
      );
      if (!records?.length) {
        setBillingRecords([]);  // Set empty state if no records are returned
        return;
      }

      const clients = await Promise.all(
        [...new Set(records.map(r => r.client_id).filter(Boolean))]
          .map(getClient)
      );

      const clientMap = clients.reduce((acc, { id, name }) => ({
        ...acc, [id]: name || 'Unknown'
      }), {});

      const formatDate = date => date &&
        new Date(date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });


      const calculatePaymentStatus = (due_date, currentStatus) => {
        const paymentStatusMap = {
          "30 days overdue": { min: 30, max: 59 },
          "60 days overdue": { min: 60, max: 89 },
          "90 days overdue": { min: 90, max: 119 },
          "120 days overdue": { min: 120, max: 149 },
          "150 days overdue": { min: 150, max: 179 },
          "180 days overdue": { min: 180, max: 9999 },
          ">180 days overdue": { min: 180, max: Infinity }
        };

        const today = new Date();
        const dueDate = new Date(due_date);
        const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));

        if (["Not Billed", "Paid", "Not Due"].includes(currentStatus)) {
          return currentStatus;
        }

        for (const [status, { min, max }] of Object.entries(paymentStatusMap)) {
          if (daysOverdue >= min && daysOverdue <= max) {
            return status;
          }
        }
        return currentStatus;
      };

      const enrichedRecords = records.map(r => {
        const now = new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" });
        const currentDate = new Date(now);
        const dueDate = new Date(r.due_date);

        // Calculate past_due and overdue_days
        const past_due = currentDate > dueDate;
        const overdue_days = past_due ? Math.floor((currentDate - dueDate) / (1000 * 60 * 60 * 24)) : 0;
        const updatedPaymentStatus = calculatePaymentStatus(r.due_date, r.payment_status);

        return {
          ...r,
          clientName: clientMap[r.client_id] || 'Unknown',
          billing_date: formatDate(r.billing_date),
          due_date: formatDate(r.due_date),
          raw_due_date: r.due_date,  // Store raw due_date for sorting
          past_due,
          overdue_days,
          payment_status: updatedPaymentStatus  // Update payment_status
        };
      });


      // Sort records by due_date (earliest to latest)
      enrichedRecords.sort((a, b) => new Date(a.raw_due_date) - new Date(b.raw_due_date));

      setBillingRecords(enrichedRecords);


      const summary = enrichedRecords.reduce((acc, record) => {
        acc.total += 1;
        if (record.payment_status === "Not Billed") acc.not_billed += 1;
        if (record.payment_status === "Billed") acc.billed += 1;
        if (record.payment_status === "Not Due") acc.not_due += 1;
        if (record.past_due) acc.over_due += 1;

        return acc;
      }, {
        total: 0,
        not_billed: 0,
        billed: 0,
        not_due: 0,
        over_due: 0
      });

      // Update state
      setSummaryCount(summary);


      setIsLoading(false)
    } catch (error) {
      console.error('Error fetching billing records:', error);
      setIsLoading(false)
    }
  };



  return (
    <ThemeProvider theme={theme}>
      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSuccessMessage("")} // Clear the success message on close
      >
        <Alert
          severity="success"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Loading Snackbar */}
      <Snackbar
        open={isLoading}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          Loading, please wait...
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setErrorMessage("")}
      >
        <Alert
          severity="error"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Box sx={{ padding: "40px 60px" }}>
        <Card
          variant="outlined"
          style={{
            marginTop: "40px",
            marginBottom: "40px",
            borderRadius: 0,
          }}
        >
          <CardHeader
            title={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center">
                  <EventIcon
                    sx={{
                      fontSize: "40px",
                      marginRight: "8px",
                    }}
                  />
                  <Typography variant="h6">Client Retention</Typography>
                </Box>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<NavigateNextIcon fontSize="small" />}
                  sx={{ color: "white" }}
                >
                  <Typography sx={{ color: "#ffffff99" }}>
                    Client Retention
                  </Typography>
                  <Typography
                    component={Link}
                    to="/manage-technicians"
                    sx={{ color: "#ffffff", textDecoration: "none" }}
                  >
                    View Client Billing Report
                  </Typography>
                </Breadcrumbs>
              </Box>
            }
            sx={{
              backgroundColor: "#1e5d45",
              color: "white",
              padding: "16px",
            }}
          />
          <CardContent sx={{ padding: "20px" }}>
            <Card variant="outlined" sx={{ padding: "20px" }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    Client Billing Report
                  </Typography>
                }
                action={
                  <Button
                    component={Link}
                    onClick={() => navigate("/create-billing-record")}
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                  >
                    Create Billing Record
                  </Button>
                }
                sx={{ marginBottom: 2 }}
              />

              <Grid item xs={12} sm={12}>
                <Grid container spacing={2}>
                  <Grid item xs={3} sm={3} md={3}></Grid> {/* Offset by 6 columns */}
                  <Grid item xs={4} sm={4} md={4}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Billing Date
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Grid container spacing={3} sx={{ mb: 2 }} alignItems="center">
                  <Grid item xs={3} sm={3} md={3}>
                    <Autocomplete
                      freeSolo
                      options={clientsCache}
                      getOptionLabel={(client) => client?.name || ""}
                      value={searchFilter.client || null}
                      onChange={(event, newValue) =>
                        handleSearchClient(event, newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client Name"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={error || ""}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={2} sm={2} md={2}>
                    <TextField
                      label="Start Date"
                      type="date"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={searchFilter.start_date}
                      onChange={(e) =>
                        setSearchFilter((prev) => ({
                          ...prev,
                          start_date: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={2} sm={2} md={2}>
                    <TextField
                      label="End Date"
                      type="date"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={searchFilter.end_date}
                      onChange={(e) =>
                        setSearchFilter((prev) => ({
                          ...prev,
                          end_date: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    sm={3}
                    md={3}
                    container
                  // justifyContent="flex-end"
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearchButtonClick}
                      disabled={isLoading}
                    >
                      {isLoading ? "Searching..." : "Search"}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={3} mt={2}>
                <Grid item xs={12}>
                  <TableContainer component={Paper}>
                    {loading ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                        <Typography variant="body1" ml={2}>Loading billing records...</Typography>
                      </Box>
                    ) : error ? (
                      <Typography color="error" align="center" mt={2}>
                        Error fetching billing records: {error}
                      </Typography>
                    ) : billingRecords.length > 0 && (
                      <>
                        <Grid item xs={12} container spacing={1} sx={{ marginBottom: 1 }}>
                          <Grid item xs={2}>
                            <Card
                              sx={{
                                backgroundColor: '#1976D2',
                                color: '#FFF',
                                borderRadius: 1,
                                textAlign: 'center',
                                padding: '4px',  // Minimal padding for a compact look
                                boxShadow: 1
                              }}
                            >
                              <CardContent sx={{ padding: '4px' }}>  {/* Reduced internal padding */}
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                                  Total Billing Records
                                </Typography>
                                <Typography variant="h5" sx={{ fontSize: '1.5rem' }}>
                                  {summaryCount.total}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={2}>
                            <Card
                              sx={{
                                backgroundColor: '#D32F2F',  // Red color
                                color: '#FFF',
                                borderRadius: 1,
                                textAlign: 'center',
                                padding: '4px',  // Minimal padding for a compact look
                                boxShadow: 1
                              }}
                            >
                              <CardContent sx={{ padding: '4px' }}>  {/* Reduced internal padding */}
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                                  Overdue
                                </Typography>
                                <Typography variant="h5" sx={{ fontSize: '1.5rem' }}>
                                  {summaryCount.over_due}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {[
                                "Client",
                                "Billing Invoice Number",
                                "Billing Date",
                                "Due Date",
                                "Overdue Days",
                                "Total Amount",
                                "Payment Status",
                                "Actions",
                              ].map((header) => (
                                <TableCell
                                  key={header}
                                  sx={{
                                    fontSize: "0.8rem",
                                    fontWeight: "bold",
                                    padding: "6px",
                                    backgroundColor: "#f7f7f7"
                                  }}
                                >
                                  {header}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {billingRecords.length > 0 ? (
                              billingRecords.map((billingRecord, index) => (
                                <TableRow key={index}
                                  sx={{
                                    backgroundColor: billingRecord.past_due ? '#FFEBEE' : 'inherit'
                                  }}>
                                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                    {billingRecord.clientName}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                    {billingRecord.billing_invoice_number}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                    {billingRecord.billing_date}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                    {billingRecord.due_date}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                    {billingRecord.overdue_days}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                    {billingRecord.total_amount} PHP
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                    {billingRecord.payment_status}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                    <Box display="flex" alignItems="center">
                                      <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => navigate(`/view-billing-record/${billingRecord.id}`)}
                                        sx={{
                                          marginRight: "10px",
                                          fontSize: "0.75rem",
                                          padding: "4px 10px",
                                        }}
                                      >
                                        View
                                      </Button>
                                      <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => navigate(`/edit-billing-record/${billingRecord.id}`)}
                                        sx={{
                                          marginRight: "10px",
                                          fontSize: "0.75rem",
                                          padding: "4px 10px",
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    </Box>
                                  </TableCell>
                                </TableRow>

                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                  No billing records found.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </>
                    )}
                  </TableContainer>

                </Grid>
              </Grid>
            </Card>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider >
  );
}

export default ViewClientBillingRecords;
